import React, {useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import classnames from 'classnames';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import {HiOutlineMenu} from '@react-icons/all-files/hi/HiOutlineMenu';
import {HiOutlineX} from '@react-icons/all-files/hi/HiOutlineX';
import {HiOutlineArchive} from '@react-icons/all-files/hi/HiOutlineArchive';
import {FaHamburger} from '@react-icons/all-files/fa/FaHamburger';
import get from 'lodash.get';
import {HomeIcon} from '@components/shared/icons/HomeIcon';
import {DocsIcon} from '@components/shared/icons/DocsIcon';
import {AnalyticsIcon} from '@components/shared/icons/AnalyticsIcon';
import Logo from '../Logo/Logo';
import UserMenu from './UserMenu/UserMenu';
import useIsPrivateRoute from '../../../hooks/useIsPrivateRoute';
import {useGet2FAEnforcementStatus} from '@hooks/useGet2FAEnforcementStatus';
import {
    selectFeatures, selectHasUserPermissionOnRoot, selectPermissions, selectUser, selectAccessToken, selectGetIsDelegation
} from '../../Login/userSlice';
import useTransactionsPageNavigate from '../../../hooks/useTransactionsPageNavigate';
import useTemplatesPageNavigate from '../../../hooks/useTemplatesPageNavigate';
import DelegationSelector from '../../Delegation/Delegation';
import permissionsService from '../../../utils/permissionsService';
import {
    DASHBOARD_PATH,
    REPORTS_PATH,
    TRANSACTIONS_PATH,
    PACKAGE_QUERY_OPTIONS,
    TRANSACTION_PATH,
    TEMPLATES_PATH,
    TEMPLATE_PATH,
    ARCHIVE_PATH,
    CONNECT_AUTHORIZE_PATH
} from '../../../../constants';

import './headerV2.less';
import LanguageSelector from '@components/common/Header/LanguageSelector/LanguageSelector';
import ThemeSelector from '@components/common/Header/ThemeSelector/ThemeSelector';
import Subaccounts from '@components/common/Header/Subaccounts/Subaccounts';
import {AppIconButton} from '@components/Core/buttons/AppIconButton/AppIconButton';

export default function HeaderV2() {
    const user = useSelector(selectUser);
    const location = useLocation();
    const navigate = useNavigate();
    const isFatIcons = get(user, 'data.fatIcons');
    const intl = useIntl();
    const {isPrivateRoute, isRouteExist} = useIsPrivateRoute();
    const features = useSelector(selectFeatures);
    const permissions = useSelector(selectPermissions);
    const hasUserPermissionOnRoot = useSelector(selectHasUserPermissionOnRoot);
    const accessToken = useSelector(selectAccessToken);
    const isDelegation = useSelector(selectGetIsDelegation);
    const isScopedUser = permissionsService.getIsScopedUser({accessToken});
    const {isBlocking: is2FASetupEnforced} = useGet2FAEnforcementStatus();
    const [isNavOpen, setIsNavOpen] = useState(false);
    const className = classnames('header', {
        'show-items': isPrivateRoute || !isRouteExist
    });
    const navigateToTransactionsWithParams = useTransactionsPageNavigate(null, true);
    const navigateToTemplatesWithParams = useTemplatesPageNavigate(null, true);

    function openNav() {
        setIsNavOpen(true);
    }
    function closeNav() {
        setIsNavOpen(false);
    }

    function navigateToTransactions(e) {
        e.preventDefault();
        navigateToTransactionsWithParams({
            query: PACKAGE_QUERY_OPTIONS.INBOX
        }, true);
        closeNav();
    }

    function navigateToTemplates(e) {
        e.preventDefault();
        navigateToTemplatesWithParams({}, true);
        closeNav();
    }

    function navigateToArchives(e) {
        e.preventDefault();
        navigate(ARCHIVE_PATH);
        closeNav();
    }

    function getIsActiveClass(routes = []) {
        const isActive = routes.some((route) => location.pathname.startsWith(route));
        return isActive ? 'is-active' : '';
    }

    const disabledNavigationClass = {'is-navigation-disabled': is2FASetupEnforced};
    const dashboardClassName = classnames('dashboard left link', getIsActiveClass([DASHBOARD_PATH]), disabledNavigationClass);
    const transactionsClassName = classnames('transactions left link wh-transactions-link', getIsActiveClass([TRANSACTIONS_PATH, TRANSACTION_PATH]), disabledNavigationClass);
    const templatesClassName = classnames('templates left link', getIsActiveClass([TEMPLATES_PATH, TEMPLATE_PATH]), disabledNavigationClass);
    const archivesClassName = classnames('archives left link', getIsActiveClass([ARCHIVE_PATH]), disabledNavigationClass);
    const reportsClassName = classnames('reports left link', getIsActiveClass([REPORTS_PATH]), disabledNavigationClass);

    const links = (
        <>
            <Link
                to={DASHBOARD_PATH}
                className={dashboardClassName}
                onClick={closeNav}
                data-wdio="test-header-go-to-dashboard-btn"
            >
                <HomeIcon className="home-icon icon" />
                {intl.formatMessage({id: 'esl.generic.dashboard', defaultMessage: ''})}
            </Link>
            {permissionsService.hasTransactionPermission({features, permissions, hasUserPermissionOnRoot}) && (
                <a
                    className={transactionsClassName}
                    onClick={navigateToTransactions}
                    href={TRANSACTIONS_PATH}
                    data-wdio="test-header-go-to-transactions-btn"
                >
                    <DocsIcon className="docs-icon icon" />
                    {intl.formatMessage({id: 'esl.generic.transactions', defaultMessage: ''})}
                </a>
            )}
            {permissionsService.hasTransactionPermission({features, permissions, hasUserPermissionOnRoot}) && (
                <a
                    className={templatesClassName}
                    onClick={navigateToTemplates}
                    href={TEMPLATES_PATH}
                    data-wdio="test-header-go-to-templates-btn"
                >
                    <DocsIcon className="docs-icon icon" />
                    {intl.formatMessage({id: 'esl.generic.templates', defaultMessage: ''})}
                </a>
            )}
            {permissionsService.hasArchivePermission({features, permissions}) && (
                <a
                    className={archivesClassName}
                    onClick={navigateToArchives}
                    href={ARCHIVE_PATH}
                    data-wdio="test-header-go-to-archives-btn"
                >
                    <HiOutlineArchive className="archive-icon icon" aria-hidden />
                    {intl.formatMessage({id: 'esl.generic.archives', defaultMessage: ''})}
                </a>
            )}
            {permissionsService.hasReportPagePermission({features, permissions, hasUserPermissionOnRoot}) && (
                <Link
                    to={REPORTS_PATH}
                    className={reportsClassName}
                    onClick={closeNav}
                    data-wdio="test-header-go-to-reports-btn"
                >
                    <AnalyticsIcon className="analytics-icon icon" />
                    {intl.formatMessage({id: 'esl.generic.reports', defaultMessage: ''})}
                </Link>
            )}
        </>
    );

    return (
        <div className="header-container-v2">
            <div className={className}>
                {!isScopedUser && (
                    <div className="navigation-icon-container">
                        <AppIconButton
                            edge="end"
                            onClick={openNav}
                            aria-label={intl.formatMessage({id: 'page.header.menu', defaultMessage: ''})}
                            data-wdio="test-header-navigation-btn"
                        >
                            {!isFatIcons ? <HiOutlineMenu /> : <FaHamburger />}
                        </AppIconButton>
                    </div>
                )}
                {!isScopedUser && (
                    <Drawer
                        anchor="left"
                        open={isNavOpen}
                        classes={{root: 'left-navigation'}}
                        onClose={closeNav}
                        data-wdio="test-header-navigation-panel-container"
                    >
                        <div className="close-icon-container">
                            <IconButton
                                edge="end"
                                onClick={closeNav}
                                color="inherit"
                            >
                                <HiOutlineX />
                            </IconButton>
                        </div>
                        <div className="menu-title">
                            {intl.formatMessage({id: 'page.header.menu', defaultMessage: ''})}
                        </div>
                        {links}
                    </Drawer>
                )}
                <Logo />
                {!isScopedUser && links}
                <div className="right-container">
                    {!isScopedUser && features?.delegation && <DelegationSelector />}
                    <LanguageSelector shouldShowIcon />
                    <ThemeSelector shouldShowIcon />
                    {!isScopedUser
                        && !isDelegation
                        && !location.pathname.startsWith(CONNECT_AUTHORIZE_PATH)
                        && <Subaccounts shouldShowIcon />}
                    <UserMenu />
                </div>
            </div>
        </div>
    );
}
